import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';
import { useState, type FormEvent } from 'react';

import { AIContentGenerationComplete } from '../../pages/Write/components/AIContentGenerationComplete';
import { AIContentGenerationLoadingOverlay } from '../../pages/Write/components/AIContentGenerationLoadingOverlay';
import { InsightsFooter } from '../../pages/Write/sections/InsightsSection/components/InsightsFooter';
import { SeekAiSection } from '../../pages/Write/sections/SeekAiSection';
import {
  OptimisationSuggestions,
  SuggestionOperation,
  type Suggestion,
} from '../OptimisationSuggestions';

import translations from './.vocab';

import * as styles from './index.css';

export interface InsightsProps {
  renderGenerateAdSection?: boolean;
}

// TODO: this is mock data and will be removed when the api integration is done
const suggestionsMock: Suggestion[] = [
  {
    id: 'suggestion_1',
    title: 'Specify Required Professional Registration',
    topic: 'Certifications',
    operation: SuggestionOperation.ADD,
    reason:
      'As this is a Clinical Exercise Physiology position, specifying required professional registration/accreditation is crucial for compliance and credibility',
    example:
      'Current ESSA Accreditation as an Accredited Exercise Physiologist (AEP) is essential for this position',
  },
  {
    id: 'suggestion_2',
    title: 'Detail Specific Teaching Responsibilities',
    topic: 'Responsibilities',
    operation: SuggestionOperation.ADD,
    reason: 'The current ad lacks specific teaching duties and expectations',
    example:
      'Key responsibilities include: delivering undergraduate clinical exercise physiology units, supervising student placements, conducting practical laboratory sessions, and participating in course accreditation activities',
  },
  {
    id: 'suggestion_3',
    title: 'Quantify Research Requirements',
    topic: 'Requirements',
    operation: SuggestionOperation.ADD,
    reason:
      'Research expectations should be clearly quantified for academic positions',
    example:
      'Demonstrated track record of peer-reviewed publications (minimum 3 in the past 5 years) and experience in securing research funding in clinical exercise physiology or related fields',
  },
  {
    id: 'suggestion_4',
    title: 'Specify Work Rights Requirements',
    topic: 'Work Eligibility',
    operation: SuggestionOperation.ADD,
    reason:
      'Clear work eligibility requirements help filter suitable candidates',
    example:
      'Candidates must have Australian work rights and be eligible for a Working with Children Check in Queensland',
  },
  {
    id: 'suggestion_5',
    title: 'Define Clinical Experience Requirements',
    topic: 'Requirements',
    operation: SuggestionOperation.ADD,
    reason:
      'Clinical experience requirements are essential for this role but currently not specified',
    example:
      'Minimum of 3 years post-qualification clinical experience as an Accredited Exercise Physiologist, with demonstrated expertise in clinical assessment and exercise prescription',
  },
];

// TODO: this function below will be replaced by the api integration
const getSuggestions = async (): Promise<Suggestion[]> =>
  new Promise((resolve) => {
    setTimeout(resolve, 1000, suggestionsMock);
  });

export const getUnusedSuggestions = (
  suggestions: Suggestion[],
  appliedSuggestions: string[],
) =>
  suggestions.filter(
    (suggestion: Suggestion) =>
      !appliedSuggestions.find(
        (appliedSuggestion: string) => appliedSuggestion === suggestion.id,
      ),
  );

export const areAllSuggestionsApplied = (
  suggestions: Suggestion[],
  appliedSuggestions: string[],
) =>
  suggestions.every((suggestion: Suggestion) =>
    appliedSuggestions.find(
      (appliedSuggestion: string) => appliedSuggestion === suggestion.id,
    ),
  );

export const Insights = ({
  renderGenerateAdSection = false,
}: InsightsProps) => {
  const { t } = useTranslations(translations);

  const [showInactiveView, setShowInactiveView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [appliedSuggestions, setAppliedSuggestions] = useState<string[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);

  const onGenerateSuggestionsClicked = async () => {
    setIsLoading(true);

    const data = await getSuggestions();
    setSuggestions(data);

    setShowInactiveView(false);
    setIsLoading(false);
  };

  const onSuggestionSelected = (e: FormEvent<HTMLInputElement>, id: string) => {
    const isChecked = (e.target as HTMLInputElement).checked;
    setSelectedSuggestions((prev) =>
      isChecked
        ? [...prev, id]
        : prev.filter((suggestionId) => suggestionId !== id),
    );
  };

  const applySuggestions = () => {
    setIsLoading(true);

    // TODO: this timeout is purely to create a delay so that the loading state is shown, should be removed during api integration
    setTimeout(() => {
      // TODO: apply selected suggestions to Job description here in later story
      setAppliedSuggestions((prev) => [...prev, ...selectedSuggestions]);
      setSelectedSuggestions([]);
      setIsLoading(false);
    }, 1000);
  };

  const hasSuggestions = Boolean(suggestions.length);
  const unusedSuggestions = getUnusedSuggestions(
    suggestions,
    appliedSuggestions,
  );
  const allSuggestionsApplied =
    hasSuggestions && areAllSuggestionsApplied(suggestions, appliedSuggestions);
  const showSuggestions =
    hasSuggestions && !allSuggestionsApplied && !isLoading;

  return (
    <Box
      borderRadius="standard"
      padding="medium"
      className={styles.container}
      display="flex"
    >
      {showInactiveView && !isLoading && (
        <SeekAiSection
          shouldRenderGenerateAdSection={renderGenerateAdSection}
          hasEnoughWordsToSuggest={true}
          onGenerateSuggestions={onGenerateSuggestionsClicked}
          onGenerateDescription={() => {}}
        />
      )}
      {isLoading && (
        <Box display="flex" justifyContent="center" width="full">
          <AIContentGenerationLoadingOverlay
            loadingText={
              showInactiveView
                ? t('generating-suggestions-text')
                : t('applying-suggestions-text')
            }
            showOverlay={true}
            withBorder={false}
          >
            <Box />
            {/* TODO: is children needed? */}
          </AIContentGenerationLoadingOverlay>
        </Box>
      )}
      {showSuggestions && (
        <Stack space="none">
          <OptimisationSuggestions
            suggestions={unusedSuggestions}
            onSuggestionSelected={onSuggestionSelected}
            selectedSuggestions={selectedSuggestions}
          />
          <InsightsFooter
            showApplyButton={true}
            applySuggestionsClicked={applySuggestions}
          />
        </Stack>
      )}
      {allSuggestionsApplied && (
        <Stack space="large">
          <AIContentGenerationComplete />
          <InsightsFooter showApplyButton={false} />
        </Stack>
      )}
    </Box>
  );
};
